import { useNavigate, useLocation } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/components/ui/utils';
import { DayTimerControl } from './DayTimerControl';
import { PRODUCT_NAME } from '../config';
import type { TimeEntry } from '../types';

interface AppTopBarProps {
  openTimer?: TimeEntry | null;
  onTimerChange?: (timer: TimeEntry | null) => void;
  userName?: string;
}

const NAV_ITEMS: { label: string; route: string }[] = [
  { label: 'Today', route: '/intern/dashboard' },
  { label: 'This Week', route: '/intern/week' },
  { label: 'Done', route: '/intern/done' },
  { label: 'Time', route: '/intern/time' },
];

export function AppTopBar({ openTimer, onTimerChange, userName }: AppTopBarProps) {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <header
      className="sticky top-0 z-30 w-full"
      style={{ background: 'var(--color-forest)', borderBottom: '1px solid var(--color-forest-light)' }}
    >
      <div className="mx-auto max-w-[880px] flex h-16 items-center gap-4 px-6">
        {/* Product name → back to the intern hub */}
        <button
          type="button"
          onClick={() => navigate('/intern')}
          aria-label={`${PRODUCT_NAME} home`}
          className="font-serif font-[600] text-[22px] leading-none select-none cursor-pointer border-0 bg-transparent p-0 transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-gold)] rounded-[2px] shrink-0"
          style={{ color: 'var(--color-gold)', fontFamily: 'var(--font-serif)' }}
        >
          {PRODUCT_NAME}
        </button>

        {/* Primary nav */}
        <nav aria-label="Intern OS sections" className="flex items-center gap-0.5 flex-1 min-w-0 overflow-x-auto">
          {NAV_ITEMS.map((item) => {
            const active = pathname === item.route || pathname.startsWith(item.route + '/');
            return (
              <button
                key={item.route}
                type="button"
                onClick={() => navigate(item.route)}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'px-2.5 py-1.5 rounded-[4px] text-[14px] font-[500] whitespace-nowrap transition-colors duration-200',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-gold)]',
                  active
                    ? 'text-[var(--color-warm-white)] bg-[rgba(245,240,232,0.12)]'
                    : 'text-[rgba(245,240,232,0.7)] hover:text-[var(--color-warm-white)] hover:bg-[rgba(245,240,232,0.08)]'
                )}
              >
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-3 shrink-0">
          {onTimerChange && (
            <DayTimerControl
              openTimer={openTimer ?? null}
              onTimerChange={onTimerChange}
            />
          )}

          {userName && (
            <span
              className="hidden md:inline text-[14px] font-[500]"
              style={{ color: 'rgba(245,240,232,0.8)' }}
            >
              {userName}
            </span>
          )}

          <Button
            variant="ghost"
            size="icon"
            onClick={handleSignOut}
            aria-label="Sign out"
            className="text-[var(--soft-gray)] hover:text-[var(--color-warm-white)] hover:bg-[rgba(245,240,232,0.1)] rounded-[4px]"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </header>
  );
}
